import { useEffect, useRef, useState } from "react";
import Sidebar from "../common/Sidebar";
import Logout from "../common/Logout";
import "../customCss/AllUsers.css";
import LoadingBar from "react-top-loading-bar";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";
import { FaEdit, FaTrash } from "react-icons/fa";
import moment from "moment";
import DataService from "../services/data.service";

const ProgramsPricing = () => {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [editPopup, setEditPopup] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [name, setName] = useState("");
  const [halfDayPrice, setHalfDayPrice] = useState("");
  const [fullDayPrice, setFullDayPrice] = useState("");
  const [deletePopup, setDeletePopup] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const getAllPrograms = async () => {
    setLoading(true);
    ref.current.continuousStart();
    try {
      const res = await DataService.getAllProgramPrices();
      const data = res?.data?.data || [];
      setPrograms(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load program prices");
    } finally {
      setLoading(false);
      ref.current.complete();
    }
  };

  useEffect(() => {
    document.title = "Program Pricing";
    getAllPrograms();
  }, []);

  const handleEdit = (program) => {
    setSelectedProgram(program);
    setName(program?.name || "");
    setHalfDayPrice(program?.halfDayPrice || "");
    setFullDayPrice(program?.fullDayPrice || "");
    setEditPopup(true);
  };

  const handleCreate = async () => {
    if (!name || !halfDayPrice || !fullDayPrice) {
      toast.error("Please fill all fields");
      return;
    }

    setButtonLoading(true);
    try {
      await DataService.createProgramPrice({
        name,
        halfDayPrice,
        fullDayPrice,
      });
      toast.success("Program created successfully");
      setEditPopup(false);
      resetForm();
      getAllPrograms();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create program");
    } finally {
      setButtonLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!name || !halfDayPrice || !fullDayPrice) {
      toast.error("Please fill all fields");
      return;
    }

    setButtonLoading(true);
    try {
      await DataService.updateProgramPrice(selectedProgram._id, {
        name,
        halfDayPrice,
        fullDayPrice,
      });
      toast.success("Program updated successfully");
      setEditPopup(false);
      resetForm();
      getAllPrograms();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update program");
    } finally {
      setButtonLoading(false);
    }
  };

  const handleDeleteClick = (program) => {
    setSelectedProgram(program);
    setDeletePopup(true);
  };

  const handleDelete = async () => {
    if (!selectedProgram?._id) return;
    setButtonLoading(true);
    try {
      await DataService.deleteProgramPrice(selectedProgram._id);
      toast.success("Program deleted successfully");
      setDeletePopup(false);
      setSelectedProgram(null);
      getAllPrograms();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete program");
    } finally {
      setButtonLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedProgram(null);
    setName("");
    setHalfDayPrice("");
    setFullDayPrice("");
  };

  const handleSubmit = () => {
    if (selectedProgram) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  return (
    <div className="main_section">
      <div className="main_flex">
        <div className="main_flexL">
          <Sidebar />
        </div>

        <div className="main_flexR">
          <div className="right_title">
            <h1>Program Pricing</h1>
            <Logout />
          </div>

          <LoadingBar color="#84C342" ref={ref} height={5} />

          <div className="filters_sec">
            <div className="filtersL"></div>
            <div className="filtersR text-end">
              <button
                id="reloadBtn"
                onClick={getAllPrograms}
                disabled={loading}
              >
                <i className={`fa fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>{" "}
                Reload
              </button>
              <button
                className="btn btn-primary ms-3"
                onClick={() => {
                  resetForm();
                  setEditPopup(true);
                }}
              >
                <i className="fas fa-plus" /> Add New
              </button>
            </div>
          </div>

          <div className="main_innerSec">
            <div className="main_booksSec">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th>#</th>
                    <th>Program Name</th>
                    <th className="text-center">Half Day Price ($)</th>
                    <th className="text-center">Full Day Price ($)</th>
                    <th>Updated At</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6">Loading...</td>
                    </tr>
                  ) : programs?.length > 0 ? (
                    programs.map((program, index) => (
                      <tr key={program._id}>
                        <td>{index + 1}</td>
                        <td>{program.name}</td>
                        <td className="text-center">${program.halfDayPrice}</td>
                        <td className="text-center">${program.fullDayPrice}</td>
                        <td>
                          {moment(program.updatedAt).format(
                            "MM/DD/YYYY hh:mm A"
                          )}
                        </td>
                        <td className="text-center">
                          <FaEdit
                            className="me-2 text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleEdit(program)}
                          />
                          <FaTrash
                            className="text-danger"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDeleteClick(program)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">No program prices found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Add / Edit Popup ===== */}
      {editPopup && (
        <div className="popOuter">
          <div className="popInner" style={{ maxWidth: "500px" }}>
            <i
              className="fas fa-times"
              onClick={() => {
                setEditPopup(false);
                resetForm();
              }}
            />
            <div className="popBody">
              <div className="popForm">
                <h2 className="mb-3">
                  {selectedProgram ? "Edit Program" : "Add New Program"}
                </h2>

                <div className="mb-3">
                  <TextField
                    label="Program Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                  />
                </div>

                <div className="mb-3">
                  <TextField
                    label="Half Day Price ($)"
                    type="number"
                    value={halfDayPrice}
                    onChange={(e) => setHalfDayPrice(e.target.value)}
                    fullWidth
                    required
                  />
                </div>

                <div className="mb-3">
                  <TextField
                    label="Full Day Price ($)"
                    type="number"
                    value={fullDayPrice}
                    onChange={(e) => setFullDayPrice(e.target.value)}
                    fullWidth
                    required
                  />
                </div>

                <div className="text-center mt-4">
                  <button
                    className="btn btn-primary"
                    disabled={buttonLoading}
                    onClick={handleSubmit}
                    style={{ color: "#fff", minWidth: "120px" }}
                  >
                    {buttonLoading ? (
                      <i className="fa fa-spinner fa-spin" />
                    ) : selectedProgram ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Delete Confirmation Popup ===== */}
      {deletePopup && (
        <div className="popOuter">
          <div
            className="popInner"
            style={{ maxWidth: "500px", minWidth: "500px" }}
          >
            <i
              className="fas fa-times"
              onClick={() => {
                setDeletePopup(false);
                setSelectedProgram(null);
              }}
            />
            <div className="popBody">
              <h4>Delete Program?</h4>
              <p className="mb-0 fw-bold">{selectedProgram?.name}</p>
              <div className="text-center mt-4">
                <button
                  className="btn btn_danger btn-primary py-2 me-2"
                  disabled={buttonLoading}
                  onClick={handleDelete}
                >
                  {buttonLoading ? (
                    <div className="spinner-border button mx-4" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
                <button
                  className="btn btn-primary py-2"
                  onClick={() => {
                    setDeletePopup(false);
                    setSelectedProgram(null);
                  }}
                >
                  No, Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramsPricing;
